// ADV-264 — one order on /account/my-orders. While the order is in progress it is the Figma
// "mcard — Bestellstatus" card (vehicle, Bestellstatus, addresses, costs); once the car is
// handed over (wonStatus 'won') it is the "mcard — Meine Bestellung" card without the steps.
// Rows only appear for values the order carries — see orderData.ts for what is left out.
// OrderStatusCard (ADV-265, the dashboard) is the in-progress card without addresses and costs.
import * as React from 'react';
import { Link } from 'react-router-dom';
import OrderSteps from './OrderSteps';
import WithdrawalBlock from './WithdrawalBlock';
import { buyingTypeOf, carSubtitle, carTitle, checkoutAddressesOf, contractTermsOf, contractUrl, customerAddressLine, deliveryAddressLine, euro, euroShort, germanDate, hasAmount, hasValue, isWon, kilometers, monthlyRateOf, orderSteps, purchaseTermsOf, purchaseTotalOf, statusKey, withdrawalStateOf, } from './orderData';
const ns = (key) => `MyOrderPage:orders.${key}`;
// Desktop: centered column (make, sub, image, REQ, condition). Mobile: text and image side by
// side, condition under a divider — the same nodes rearranged by MyOrdersNextPage.css.
const VehicleHead = ({ car, request, condition, imageUrl }) => {
    const href = `/search/vehicle/${car._id}`;
    const subtitle = carSubtitle(car);
    return (React.createElement("div", { className: "ao-vehicle" },
        React.createElement("div", { className: "ao-vehicle-text" },
            React.createElement(Link, { className: "ao-vehicle-make", to: href, translate: "no" }, carTitle(car)),
            subtitle ? React.createElement("span", { className: "ao-vehicle-sub", translate: "no" }, subtitle) : null,
            request ? React.createElement("span", { className: "ao-vehicle-req", translate: "no" }, request) : null),
        imageUrl ? (React.createElement(Link, { className: "ao-vehicle-img-link", to: href, tabIndex: -1, "aria-hidden": "true" },
            React.createElement("img", { className: "ao-vehicle-img", src: imageUrl, alt: "", loading: "lazy", decoding: "async" }))) : null,
        condition ? React.createElement("span", { className: "ao-vehicle-cond" }, condition) : null));
};
// the addresses as the customer entered them in the checkout; Odoo's copy without a saved checkout
const Addresses = ({ lead, contract, t }) => {
    const checkout = checkoutAddressesOf(lead, contract);
    const selfPickup = checkout ? checkout.selfPickup : lead.selfPickup;
    const billing = checkout ? checkout.billing : customerAddressLine(lead.customerAddress);
    let delivery = checkout ? checkout.delivery : deliveryAddressLine(lead.deliveryAddress);
    if (selfPickup)
        delivery = t(ns('selfPickup'));
    if (!billing && !delivery)
        return null;
    return (React.createElement("div", { className: "ao-adr" },
        billing ? React.createElement("span", { className: "ao-adr-label" }, t(ns('billingAddress'))) : null,
        billing ? React.createElement("span", { className: "ao-adr-value" }, billing) : null,
        delivery ? React.createElement("span", { className: "ao-adr-label" }, t(ns('deliveryLabel'))) : null,
        delivery ? React.createElement("span", { className: "ao-adr-value" }, delivery) : null));
};
// 'inline; filename="Kaufvertrag_….pdf"' → 'Kaufvertrag_….pdf'
const fileNameOf = (disposition) => {
    const match = /filename="?([^";]+)"?/i.exec(disposition || '');
    return match ? match[1] : '';
};
// Fetches the PDF first (the contract takes a few seconds to generate), so the button knows
// when the download is ready, then saves it under the server's file name.
const saveFile = async (href) => {
    const response = await fetch(href);
    if (!response.ok)
        throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileNameOf(response.headers.get('content-disposition')) || 'Kaufvertrag.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
    // revoked a moment later: some browsers start reading the blob only after click() returns
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
// Figma "ord-doc" (in-progress orders only — the handed-over card has none): the contract PDF.
// While it downloads the button shows a spinner and ignores further clicks; it stays a real
// link, so "save link as" and a click without JS still work.
const ContractRow = ({ href, t, onError }) => {
    const [loading, setLoading] = React.useState(false);
    const busy = React.useRef(false);
    const download = async (event) => {
        event.preventDefault();
        if (busy.current)
            return;
        busy.current = true;
        setLoading(true);
        try {
            await saveFile(href);
        }
        catch {
            onError();
        }
        finally {
            busy.current = false;
            setLoading(false);
        }
    };
    return (React.createElement("div", { className: "ao-doc" },
        React.createElement("span", { className: "ao-doc-label" }, t(ns('contractLabel'))),
        React.createElement("a", { className: loading ? 'ao-doc-btn ao-doc-btn--loading' : 'ao-doc-btn', href: href, download: true, onClick: download, "aria-busy": loading, "aria-disabled": loading },
            React.createElement("span", { className: "ao-doc-btn-label" }, t(ns('contractDownload'))),
            loading ? React.createElement("span", { className: "ao-doc-spinner", "aria-hidden": "true" }) : null)));
};
const CostRow = ({ label, value, total }) => (React.createElement("div", { className: total ? 'ao-row ao-row--total' : 'ao-row' },
    React.createElement("span", { className: "ao-row-label" }, label),
    React.createElement("span", { className: "ao-row-value" }, value)));
// monthly amounts in whole euros, as the checkout shows them ("+25 € / Monat", "16 € mtl.", "297 €")
const perMonth = (amount, t) => t(ns('amountPerMonth'), { amount: euroShort(amount) });
// Figma's financing/leasing rows from the checkout's saved calculation: its Bestellübersicht split
// of the Gesamtrate (vehicle rate, Anschluss-Garantie, Rundum-Sorglos-Paket) and the terms around
// it. "—" where the order has none (no Anschluss-Garantie), as in Figma. The 2. Kreditnehmer /
// Leasingnehmer row only when the checkout had one (user's call, 2026-10-21).
const checkoutRows = (terms, kind, t) => {
    const none = t(ns('dlg.notAvailable'));
    const rows = [
        { label: t(ns('term')), value: t(ns('months'), { count: terms.term }) },
        { label: t(ns(kind === 'financing' ? 'vehicleRate' : 'leasingRate')), value: perMonth(terms.vehicleRate, t) },
    ];
    if (kind === 'financing')
        rows.push({ label: t(ns('downPayment')), value: euro(terms.down) });
    else
        rows.push({ label: t(ns('mileagePerYear')), value: hasAmount(terms.annualMileage) ? kilometers(terms.annualMileage) : none });
    rows.push(terms.warrantyYears
        ? { label: t(ns('warranty'), { duration: t(ns('warrantyYears'), { count: terms.warrantyYears }) }), value: perMonth(terms.warrantyMonthly, t) }
        : { label: t(ns('warrantyNone')), value: none });
    if (terms.secondParty)
        rows.push({ label: t(ns(kind === 'financing' ? 'secondBorrower' : 'secondLessee')), value: terms.secondParty });
    // financed with the rates: its monthly share; else paid once at the order
    let pack = none;
    if (terms.withAddServices && hasAmount(terms.packageMonthly))
        pack = perMonth(terms.packageMonthly, t);
    if (!terms.withAddServices && hasAmount(terms.addServicesCostRaw))
        pack = t(ns('amountOnce'), { amount: euro(terms.addServicesCostRaw) });
    rows.push({ label: t(ns('package')), value: pack });
    return rows;
};
const warrantyLabel = (lead, t) => {
    const warranty = lead.warranty || {};
    const period = warranty.warrantyPeriod ? t(`MyOrderPage:${warranty.warrantyPeriod}year`, { warrantyPeriod: warranty.warrantyPeriod }) : '';
    const pack = warranty.warrantyPackage ? t(`MyOrderPage:${warranty.warrantyPackage}`) : '';
    return [period, pack].filter(Boolean).join(' ');
};
// Figma's purchase rows from the checkout's saved calculation: the car without the Rundum-Sorglos-
// Paket, the Anschluss-Garantie and the package as its own line; they add up to the checkout's
// Gesamtkaufpreis. (Odoo's "Aufbereitungskosten" is that same package again, so it isn't listed.)
const purchaseRows = (terms, t) => [
    { label: t(ns('vehiclePrice')), value: euro(terms.vehiclePrice) },
    terms.warrantyYears
        ? { label: t(ns('warranty'), { duration: t(ns('warrantyYears'), { count: terms.warrantyYears }) }), value: euro(terms.warranty) }
        : { label: t(ns('warrantyNone')), value: t(ns('dlg.notAvailable')) },
    { label: t(ns('package')), value: hasAmount(terms.package) ? euro(terms.package) : t(ns('packageIncluded')) },
];
// Without a saved calculation: the order's own lines (Odoo).
const orderPurchaseRows = (lead, t) => {
    const rows = [];
    if (hasAmount(lead.currentSalesPrice))
        rows.push({ label: t(ns('vehiclePrice')), value: euro(lead.currentSalesPrice) });
    if (lead.warranty && hasAmount(lead.warranty.warrantyCost))
        rows.push({ label: warrantyLabel(lead, t), value: euro(lead.warranty.warrantyCost) });
    if (hasAmount(lead.preparationCost))
        rows.push({ label: t(ns('preparationCost')), value: euro(lead.preparationCost) });
    if (hasAmount(lead.extrasSumCost))
        rows.push({ label: t(ns('extrasCost')), value: euro(lead.extrasSumCost) });
    if (lead.registration && hasAmount(lead.registrationCost))
        rows.push({ label: t(ns('registrationCost')), value: euro(lead.registrationCost) });
    if (lead.isLicensePlateFee && hasAmount(lead.licensePlateCost))
        rows.push({ label: t(ns('licensePlateCost')), value: euro(lead.licensePlateCost) });
    if (lead.delivery && !lead.selfPickup && hasAmount(lead.transportationCost))
        rows.push({ label: t(ns('deliveryCost')), value: euro(lead.transportationCost) });
    return rows;
};
// Figma "ord-cost": headline figure, its sub line, the cost table, the legal line and — for
// financing/leasing — the note that opens the details layer. All three show the checkout's saved
// calculation when it matches the order; without one only the order's own figures (Odoo).
const CostSummary = ({ lead, type, contract, t, onShowDetails }) => {
    if (type === 'buy') {
        const purchase = purchaseTermsOf(lead, contract);
        const total = purchase ? purchase.total : lead.totalPrice;
        const rows = purchase ? purchaseRows(purchase, t) : orderPurchaseRows(lead, t);
        return (React.createElement(React.Fragment, null,
            React.createElement("span", { className: "ao-cost-label" }, t(ns('totalPrice'))),
            hasAmount(total) ? (React.createElement("div", { className: "ao-cost-amount" },
                React.createElement("span", { className: "ao-cost-amount-value" }, euroShort(total)))) : null,
            React.createElement("span", { className: "ao-cost-sub" }, t(ns('vatIncluded'))),
            React.createElement("div", { className: "ao-cost-spacer" }),
            React.createElement("div", { className: "ao-divider" }),
            rows.map((row) => React.createElement(CostRow, { key: row.label, ...row })),
            hasAmount(total) ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "ao-divider" }),
                React.createElement(CostRow, { label: t(ns('priceTotal')), value: euro(total), total: true }),
                React.createElement("p", { className: "ao-cost-legal" }, t(ns('legalBuy'), { total: euro(total) })))) : null));
    }
    const kind = type;
    const openDetails = () => onShowDetails(lead, kind);
    const terms = contractTermsOf(lead, kind, contract);
    const rate = terms ? terms.total : lead.monthlyInstallment;
    const term = terms ? terms.term : lead.paybackPeriod;
    const deposit = terms ? terms.down : lead.deposit;
    const subParts = [];
    if (hasValue(deposit))
        subParts.push(t(ns(kind === 'financing' ? 'subDownPayment' : 'subSpecialPayment'), { amount: euroShort(deposit) }));
    if (hasAmount(term))
        subParts.push(t(ns('months'), { count: term }));
    let rows = [];
    if (terms)
        rows = checkoutRows(terms, kind, t);
    else {
        if (hasAmount(term))
            rows.push({ label: t(ns('term')), value: t(ns('months'), { count: term }) });
        if (kind === 'financing' && hasValue(deposit))
            rows.push({ label: t(ns('downPayment')), value: euro(deposit) });
    }
    let legal = '';
    if (kind === 'financing') {
        // the checkout's Gesamtkaufpreis; Odoo's product sum without a saved calculation
        const purchase = terms ? terms.purchasePrice : lead.totalPrice;
        if (hasAmount(purchase))
            legal = t(ns('legalFinancing'), { total: euro(purchase) });
        if (terms && !terms.withAddServices && hasAmount(terms.addServicesCostRaw)) {
            legal = [legal, t(ns('legalPackageOnce'), { amount: euro(terms.addServicesCostRaw) })].filter(Boolean).join(' ');
        }
    }
    if (kind === 'leasing' && hasAmount(term) && hasAmount(rate)) {
        legal = t(ns('legalLeasing'), { sum: euro(term * rate), count: term, rate: euro(rate) });
        if (hasValue(deposit))
            legal = `${legal} ${t(ns('legalLeasingDeposit'), { deposit: euro(deposit) })}`;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { className: "ao-cost-label" }, t(ns('totalRate'))),
        hasAmount(rate) ? (React.createElement("div", { className: "ao-cost-amount" },
            React.createElement("span", { className: "ao-cost-amount-value" }, euroShort(rate)),
            React.createElement("span", { className: "ao-cost-amount-unit" }, t(ns('perMonth'))))) : null,
        React.createElement("span", { className: "ao-cost-sub" },
            subParts.map((part) => `${part} · `).join(''),
            React.createElement("button", { type: "button", className: "ao-text-btn", onClick: openDetails }, t(ns('details')))),
        React.createElement("div", { className: "ao-cost-spacer" }),
        React.createElement("div", { className: "ao-divider" }),
        rows.map((row) => React.createElement(CostRow, { key: row.label, ...row })),
        hasAmount(rate) ? (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "ao-divider" }),
            React.createElement(CostRow, { label: t(ns('rateTotal')), value: perMonth(rate, t), total: true }))) : null,
        legal ? React.createElement("p", { className: "ao-cost-legal" }, legal) : null,
        React.createElement("div", { className: "ao-hint-wrap" },
            React.createElement("div", { className: "ao-hint" },
                React.createElement("span", { className: "ao-hint-text" }, t(ns(kind === 'financing' ? 'hintFinancing' : 'hintLeasing'))),
                React.createElement("button", { type: "button", className: "ao-hint-link", onClick: openDetails }, t(ns(kind === 'financing' ? 'financingDetails' : 'leasingDetails')))))));
};
/** The condition line's figure, the same as the cost column's headline: Gesamtrate or Gesamtpreis. */
const conditionAmountOf = (lead, contract) => buyingTypeOf(lead) === 'buy' ? purchaseTotalOf(lead, contract) : monthlyRateOf(lead, contract);
const conditionLine = (lead, type, amount, t) => {
    if (!hasAmount(amount))
        return '';
    if (type === 'buy')
        return t(ns('conditionBuy'), { price: euroShort(amount) });
    return t(ns(type === 'financing' ? 'conditionFinancing' : 'conditionLeasing'), { rate: euroShort(amount) });
};
// 'small' is 400×300: enough for the 190×139 slot at 2x.
const imageUrlOf = (car, imagesStore) => {
    const mainImageId = car.metaData && car.metaData.mainImageId;
    return mainImageId ? imagesStore.getSupportedImageFormat(car._id, mainImageId, 'small') : '';
};
// Figma "Konto / Sektionskopf" + "acc-order" of an order in progress: the status, the vehicle
// and the steps.
const StatusTop = ({ lead, contract, t, imageUrl }) => {
    const checkout = checkoutAddressesOf(lead, contract);
    const selfPickup = checkout ? checkout.selfPickup : !!lead.selfPickup;
    return (React.createElement(React.Fragment, null,
        React.createElement("h2", { className: "ao-card-head" },
            React.createElement("span", { className: "ao-card-head-label" }, t(ns('statusLabel'))),
            React.createElement("span", { className: "ao-card-head-value" }, t(`DealerDashboardPage:orderStatus.selector_status_${statusKey(lead.status)}`))),
        React.createElement("div", { className: "ao-order" },
            React.createElement(VehicleHead, { car: lead.car, request: lead.request, imageUrl: imageUrl, condition: conditionLine(lead, buyingTypeOf(lead), conditionAmountOf(lead, contract), t) }),
            React.createElement(OrderSteps, { steps: orderSteps(lead, selfPickup), t: t }))));
};
/** Figma "mcard — Bestellung erfolgreich" (dashboard): the status card without addresses and costs. */
export const OrderStatusCard = ({ lead, contract, t, imagesStore }) => (React.createElement("section", { className: "ao-card" },
    React.createElement(StatusTop, { lead: lead, contract: contract, t: t, imageUrl: imageUrlOf(lead.car, imagesStore) })));
const OrderCard = ({ lead, contract, t, imagesStore, onShowDetails, onContractError, seller, onWithdraw }) => {
    const { car } = lead;
    const type = buyingTypeOf(lead);
    const imageUrl = imageUrlOf(car, imagesStore);
    const costs = React.createElement(CostSummary, { lead: lead, type: type, contract: contract, t: t, onShowDetails: onShowDetails });
    const contractHref = contractUrl(lead, contract);
    const withdrawalState = withdrawalStateOf(lead);
    const withdrawal = withdrawalState ? (React.createElement(WithdrawalBlock, { lead: lead, state: withdrawalState, seller: seller, t: t, onOpen: onWithdraw })) : null;
    if (isWon(lead)) {
        const handedOver = germanDate(lead.deliveryDate);
        return (React.createElement("section", { className: "ao-card" },
            React.createElement("h2", { className: "ao-card-head ao-card-head--title" }, t(ns('titleWon'))),
            React.createElement("div", { className: "ao-won" },
                React.createElement(VehicleHead, { car: car, request: lead.request, imageUrl: imageUrl, condition: handedOver ? t(ns('handedOverOn'), { date: handedOver }) : '' }),
                React.createElement("div", { className: "ao-col" },
                    React.createElement(Addresses, { lead: lead, contract: contract, t: t }),
                    withdrawal),
                React.createElement("div", { className: "ao-col ao-col--cost" }, costs))));
    }
    return (React.createElement("section", { className: "ao-card" },
        React.createElement(StatusTop, { lead: lead, contract: contract, t: t, imageUrl: imageUrl }),
        React.createElement("div", { className: "ao-body" },
            React.createElement("div", { className: "ao-col" },
                React.createElement(Addresses, { lead: lead, contract: contract, t: t }),
                React.createElement("div", { className: "ao-divider" }),
                contractHref ? React.createElement(ContractRow, { href: contractHref, t: t, onError: onContractError }) : null,
                React.createElement("p", { className: "ao-legal" }, t(ns('legalAgb'))),
                withdrawal),
            React.createElement("div", { className: "ao-col ao-col--cost" }, costs))));
};
export default OrderCard;
